/**
 * Plateau Hub — Style Apple tactile pro
 *
 * Route : /enseignant/terrain
 *
 * Refonte juin 2026 : remplace l'ancienne grille d'évaluation par une
 * bibliothèque de fiches REMC plateau (5 fiches initiales C1/C2).
 * Chaque fiche ouvre /enseignant/terrain/:slug avec PedagogicalCanvas + mode présentation.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Flag,
  ArrowRight,
  Filter,
  Sparkles,
  Layers,
  Wand2,
  Eye,
  Play,
  CornerDownLeft,
  RotateCw,
  Octagon,
  Waves,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { FICHES_PLATEAU, PLATEAU_CATEGORIES } from '../data/teacher/fichesPlateau';
import { hasPremium } from '../data/teacher/fichesInteractives';
import useSeo from '../hooks/useSeo';
import AudienceContextBanner from '../components/AudienceContextBanner';

const ICONS = { Play, CornerDownLeft, RotateCw, Octagon, Waves };

function FicheCard({ fiche }) {
  const Icon = ICONS[fiche.icon] || Flag;
  const premium = hasPremium(fiche.slug);
  return (
    <Link
      to={`/enseignant/terrain/${fiche.slug}`}
      data-testid={`fiche-card-${fiche.slug}`}
      className="group relative overflow-hidden rounded-3xl bg-card border border-border/60 p-5 sm:p-6 flex flex-col min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98]"
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -16px rgba(0,0,0,0.12)' }}
    >
      <div
        className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-15 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
        style={{ background: fiche.accent }}
        aria-hidden="true"
      />
      <div
        className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(90deg, transparent, ${fiche.accent}, transparent)` }}
        aria-hidden="true"
      />
      {premium && (
        <div
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-widest text-white shadow-lg"
          style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
          data-testid={`badge-premium-${fiche.slug}`}
        >
          <Sparkles className="w-3 h-3" /> Premium
        </div>
      )}
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
            style={{ background: fiche.accent, boxShadow: `0 8px 20px -8px ${fiche.accent}55` }}
          >
            <Icon className="w-6 h-6" strokeWidth={2.2} />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className="text-[10px] font-black uppercase tracking-[0.14em] rounded-full px-2.5 py-1"
              style={{ background: `${fiche.accent}1a`, color: fiche.accent }}
            >
              {fiche.competence}
            </span>
            <span className="text-[10px] text-muted-foreground font-bold">{fiche.duree}</span>
          </div>
        </div>
        <h3 className="font-black text-base sm:text-lg leading-tight tracking-tight">{fiche.title}</h3>
        <div
          className="text-[10px] font-black uppercase tracking-[0.14em] mt-1.5"
          style={{ color: fiche.accent }}
        >
          {fiche.competenceLabel}
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground mt-2.5 leading-relaxed flex-1">
          {fiche.summary}
        </p>
        <div
          className="flex items-center gap-1.5 text-xs sm:text-sm font-bold mt-4 group-hover:gap-2.5 transition-all duration-300"
          style={{ color: fiche.accent }}
        >
          Ouvrir la fiche <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </div>
      </div>
    </Link>
  );
}

export default function PlateauHub() {
  useSeo({
    title: 'Cours plateau · Fiches REMC moto · Code 2 Roues',
    description:
      'Fiches séances plateau conformes au livret REMC catégorie A : démarrer, freiner, demi-tour, slalom, virages au ralenti. Avec mode présentation pour la classe.',
    path: '/enseignant/terrain',
  });

  const [activeCat, setActiveCat] = useState('all');

  const filtered = useMemo(() => {
    if (activeCat === 'all') return FICHES_PLATEAU;
    return FICHES_PLATEAU.filter((f) => f.competenceCat === activeCat);
  }, [activeCat]);

  const counts = useMemo(() => {
    const c = { all: FICHES_PLATEAU.length };
    PLATEAU_CATEGORIES.forEach((cat) => {
      c[cat.id] = FICHES_PLATEAU.filter((f) => f.competenceCat === cat.id).length;
    });
    return c;
  }, []);

  return (
    <div className="space-y-6" data-testid="plateau-hub">
      {/* HERO style Apple tactile pro */}
      <section
        className="relative overflow-hidden rounded-3xl bg-card border border-border/60"
        data-testid="plateau-hero"
        style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 16px 40px -20px rgba(234,88,12,0.25)' }}
      >
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: '#ea580c' }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl opacity-15 pointer-events-none"
          style={{ background: '#1d4ed8' }}
          aria-hidden="true"
        />
        <div className="relative p-6 sm:p-9">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#ea580c]/15 border border-[#ea580c]/30 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[#ea580c] mb-4">
            <Flag className="w-3.5 h-3.5" /> Plateau · Maniement & maîtrise
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="plateau-title"
          >
            Donner cours sur le plateau,<br />
            <span className="text-[#ea580c]">prêt en 3 clics.</span>
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-3xl leading-relaxed">
            {FICHES_PLATEAU.length} fiches séance plateau <strong>conformes livret REMC</strong>{' '}
            (C1 maniement · C2 appréhension de la route). Cours · démo · schéma · critères ·
            évaluation dynamique · autonomie · bilan — tout est prêt pour ta séance.
          </p>

          {/* Mini-stats */}
          <div
            className="mt-5 grid grid-cols-3 gap-2 sm:gap-3 max-w-md"
            data-testid="hero-stats"
          >
            {[
              { num: FICHES_PLATEAU.length, label: 'Fiches', color: '#ea580c' },
              { num: counts.C1 || 0, label: 'C1 Maniement', color: '#1d4ed8' },
              { num: counts.C2 || 0, label: 'C2 Route', color: '#7c3aed' },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-2xl bg-background/40 border border-border/40 backdrop-blur p-2.5 sm:p-3 text-center"
              >
                <div
                  className="text-2xl sm:text-3xl font-black tabular-nums leading-none"
                  style={{ color: s.color, fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {s.num}
                </div>
                <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/65 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bandeau d'adaptation par audience (visible enseignant + futur enseignant) */}
      <AudienceContextBanner
        pageHint="Le plateau est l'épreuve de maîtrise du véhicule — 12 exercices officiels."
        contexts={{
          enseignant: {
            title: 'Tes fiches pour piloter une séance plateau',
            bullets: [
              'Chaque fiche suit la trame officielle ECSR conforme livret REMC.',
              'Mode présentation pour faire la démo, schéma SVG pour expliquer la trajectoire.',
              'Lance une Séance Live pour piloter l\'élève en temps réel.',
            ],
            cta: 'Voir les 12 exercices officiels',
            ctaTo: '/enseignant/exercices-plateau',
          },
          'futur-enseignant': {
            title: 'Modèles de fiches plateau pour ton oral CCS',
            bullets: [
              'Étudie comment chaque fiche structure la séance : cours → démo → exercice → évaluation.',
              'Repère le vocabulaire officiel REMC (C1-a → C1-g) attendu par le jury.',
              'Réutilise la trame pour ta propre fiche séance d\'examen.',
            ],
            cta: 'Aller au Hub Préparation CCS',
            ctaTo: '/preparation-ccs',
          },
        }}
        showOnEleve={false}
      />

      {/* QUICK ACTIONS XL — accès rapides */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="plateau-quick-actions">
        <Link
          to="/enseignant/eval-plateau"
          className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 flex items-center gap-3 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
            boxShadow: '0 8px 24px -10px #ea580c77',
          }}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Flag className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.3} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] opacity-85">Évaluer · 50 min</div>
            <div className="text-base sm:text-lg font-black leading-tight">Fiche éval. CCS plateau</div>
          </div>
          <ArrowRight className="w-5 h-5 text-white" strokeWidth={2.5} />
        </Link>
        <Link
          to="/enseignant/canevas-pedagogique"
          className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 flex items-center gap-3 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
            boxShadow: '0 8px 24px -10px #1d4ed877',
          }}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Eye className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.3} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] opacity-85">Découvrir</div>
            <div className="text-base sm:text-lg font-black leading-tight">Voir le canevas type</div>
          </div>
          <ArrowRight className="w-5 h-5 text-white" strokeWidth={2.5} />
        </Link>
        <Link
          to="/enseignant/creer-fiche"
          className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 flex items-center gap-3 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            boxShadow: '0 8px 24px -10px #16a34a77',
          }}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.3} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] opacity-85">Créer</div>
            <div className="text-base sm:text-lg font-black leading-tight">Ouvrir le wizard</div>
          </div>
          <ArrowRight className="w-5 h-5 text-white" strokeWidth={2.5} />
        </Link>
      </section>

      {/* FILTRES catégories */}
      <section className="rounded-2xl bg-card border border-border/60 p-3 sm:p-4" data-testid="cat-filters">
        <div className="flex items-center gap-1.5 mb-2 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
          <Filter className="w-3 h-3" /> Filtrer par compétence REMC
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setActiveCat('all')}
            data-testid="cat-filter-all"
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
              activeCat === 'all'
                ? 'bg-foreground text-background border-foreground'
                : 'bg-background border-border hover:border-foreground/40'
            }`}
          >
            Toutes · {counts.all}
          </button>
          {PLATEAU_CATEGORIES.map((cat) => {
            const active = activeCat === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCat(cat.id)}
                data-testid={`cat-filter-${cat.id}`}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
                  active ? 'text-white border-transparent' : 'bg-background border-border hover:border-foreground/40'
                }`}
                style={active ? { background: cat.color } : undefined}
              >
                {cat.label} · {counts[cat.id] || 0}
              </button>
            );
          })}
        </div>
        {activeCat !== 'all' && (
          <p className="text-[11px] italic text-muted-foreground mt-2">
            {PLATEAU_CATEGORIES.find((c) => c.id === activeCat)?.intent}
          </p>
        )}
      </section>

      {/* GRILLE FICHES */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
        data-testid="fiches-grid"
      >
        {filtered.map((fiche) => (
          <FicheCard key={fiche.slug} fiche={fiche} />
        ))}
      </section>

      {/* CTA bas */}
      <div className="rounded-2xl border-2 border-dashed border-[#ea580c]/40 p-5 sm:p-6 text-center">
        <Sparkles className="w-7 h-7 text-[#ea580c] mx-auto mb-2" />
        <h3 className="text-base sm:text-lg font-black mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          D&apos;autres fiches plateau ?
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-3">
          {FICHES_PLATEAU.length} fiches officielles aujourd&apos;hui. Pour préparer une séance hors-cadre, ouvre le wizard.
        </p>
        <Button asChild className="gap-1.5" style={{ background: '#ea580c' }} data-testid="cta-wizard">
          <Link to="/enseignant/creer-fiche">
            <Wand2 className="w-4 h-4" /> Créer ma fiche sur-mesure
          </Link>
        </Button>
      </div>
    </div>
  );
}
